import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styles from "./Filmstrip.module.css";

function PageTab({ page, index, isActive, onSelect, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: page.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.pageTab} ${isActive ? styles.pageTabActive : ""} ${
        isDragging ? styles.dragging : ""
      }`}
      {...attributes}
      {...listeners}
      onClick={() => onSelect(page.id)}
    >
      <span className={`${styles.pageTabNumber} text-label-sm`}>{index + 1}</span>
      <span className={`${styles.pageTabTitle} text-label-md`}>
        {page.title || `Página ${index + 1}`}
      </span>
      <button
        className={styles.pageTabDelete}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(page.id);
        }}
        title="Eliminar página"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
          close
        </span>
      </button>
    </div>
  );
}

function PanelThumb({ panel, index, isActive, onSelect }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: panel.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.panelThumb} ${isActive ? styles.panelThumbActive : ""} ${
        isDragging ? styles.dragging : ""
      }`}
      {...attributes}
      {...listeners}
      onClick={() => onSelect(panel.id)}
    >
      <div className={`${styles.panelThumbHeader} text-label-sm`}>
        <span>P{String(index + 1).padStart(2, "0")}</span>
        <span>{panel.duration?.toFixed(1)}s</span>
      </div>
      <div
        className={styles.panelThumbImage}
        style={{ backgroundImage: `url(${panel.image})` }}
      />
    </div>
  );
}

export default function Filmstrip({
  storyboard,
  currentPageId,
  onSelectPage,
  onDeletePage,
  onReorderPages,
  onAddPage,
  pageData,
  selectedPanelId,
  onSelectPanel,
  onReorderPanels,
  onAddPanel,
}) {
  // distance mínima antes de iniciar un drag: sin esto, dnd-kit capturaba
  // cualquier click (incluso sin arrastrar) como el inicio de un drag y se
  // comía el onClick de seleccionar página/frame.
  const pageSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const panelSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <div className={styles.wrapper}>
      {/* Tira de páginas */}
      <DndContext
        sensors={pageSensors}
        collisionDetection={closestCenter}
        onDragEnd={(e) => onReorderPages(e.active.id, e.over?.id)}
      >
        <SortableContext items={storyboard.map((p) => p.id)} strategy={rectSortingStrategy}>
          <div className={`${styles.pageRow} themed-scroll`}>
            {storyboard.map((page, index) => (
              <PageTab
                key={page.id}
                page={page}
                index={index}
                isActive={page.id === currentPageId}
                onSelect={onSelectPage}
                onDelete={onDeletePage}
              />
            ))}
            <button className={`${styles.addPageButton} text-label-sm`} onClick={onAddPage}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
                add
              </span>
              Página
            </button>
          </div>
        </SortableContext>
      </DndContext>

      {/* Tira de frames de la página activa */}
      <DndContext
        sensors={panelSensors}
        collisionDetection={closestCenter}
        onDragEnd={(e) => onReorderPanels(e.active.id, e.over?.id)}
      >
        <SortableContext
          items={pageData.panels.map((p) => p.id)}
          strategy={rectSortingStrategy}
        >
          <div className={`${styles.panelRow} themed-scroll`}>
            {pageData.panels.map((panel, index) => (
              <PanelThumb
                key={panel.id}
                panel={panel}
                index={index}
                isActive={panel.id === selectedPanelId}
                onSelect={onSelectPanel}
              />
            ))}
            <button className={styles.addPanelCard} onClick={onAddPanel}>
              <span className="material-symbols-outlined" style={{ fontSize: 28 }}>
                add
              </span>
              <span className="text-label-sm">Agregar frame</span>
            </button>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
